// aidepo-6 — ChangesScreen: журнал изменений имущества.
// aidepo-20 — записи сгруппированы ПО ОБЪЕКТАМ. Каждый объект — квартира,
// гараж, велосипед, оборудование, комната — отдельная карточка-раздел;
// под ним перечислены все изменения именно этого объекта (создан / перемещён /
// изменён / удалён / менялся регламент). Не плоский список действий, а список
// объектов, у каждого своя история.
//
// Источник — GET /api/v1/changes (универсальный журнал ChangeRecord). Бэкенд
// (ChangesController) обогащает каждую запись objectKind / objectId / objectLabel
// — типом, id и именем группировочного объекта. Для правила ТО это
// родительское оборудование. Если у записи объект не определился — она падает
// в раздел «Прочее».
//
// Вход — с главной и кнопкой в разделе «Имущество». Чат с ассистентом — другой
// экран. Тапнул запись с «можно отменить» → экран отката.

import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { T, Icon, Avatar, type IconName } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import { undo as changes, humanizeError } from '../../api';
import type { ChangeKind, ChangeRecordRow } from '../../api/endpoints/undo.js';

export interface ChangesScreenProps {
  readonly router: RouterApi;
}

type Status = 'loading' | 'ready' | 'error';

const KIND_META: Record<ChangeKind, { readonly icon: IconName; readonly verb: string }> = {
  create: { icon: 'plus', verb: 'Добавлено' },
  move: { icon: 'move', verb: 'Перемещено' },
  update: { icon: 'edit', verb: 'Изменено' },
  delete: { icon: 'trash', verb: 'Удалено' },
  destruct: { icon: 'trash', verb: 'Удалено вместе с содержимым' },
};

const OBJECT_KIND_ICON: Record<string, IconName> = {
  item: 'item',
  room: 'folder',
  equipment: 'boiler',
  property: 'home',
  scheduleRule: 'cal',
};

const OBJECT_KIND_RU: Record<string, string> = {
  item: 'вещь',
  room: 'помещение',
  equipment: 'оборудование',
  property: 'собственность',
  scheduleRule: 'правило ТО',
};

function objectIcon(kind: string | null | undefined): IconName {
  if (kind != null && OBJECT_KIND_ICON[kind] !== undefined) return OBJECT_KIND_ICON[kind] as IconName;
  return 'history';
}

interface ObjectGroup {
  readonly key: string;
  readonly objectKind: string | null;
  readonly objectId: string | null;
  readonly objectLabel: string;
  readonly rows: readonly ChangeRecordRow[]; // newest-first
  readonly latestAt: number;
}

/** Группировка по (objectKind, objectId). Записи без объекта — в спец-группу. */
function groupByObject(rows: readonly ChangeRecordRow[]): readonly ObjectGroup[] {
  const map = new Map<
    string,
    { kind: string | null; id: string | null; label: string; rows: ChangeRecordRow[] }
  >();
  for (const row of rows) {
    const hasObject =
      row.objectKind != null &&
      row.objectId != null &&
      row.objectKind !== '' &&
      row.objectId !== '';
    const key = hasObject ? `${row.objectKind}:${row.objectId}` : '__unknown__';
    const label = hasObject
      ? row.objectLabel != null && row.objectLabel.trim().length > 0
        ? row.objectLabel.trim()
        : OBJECT_KIND_RU[row.objectKind as string] ?? 'Объект'
      : 'Прочее';
    let g = map.get(key);
    if (!g) {
      g = {
        kind: hasObject ? (row.objectKind as string) : null,
        id: hasObject ? (row.objectId as string) : null,
        label,
        rows: [],
      };
      map.set(key, g);
    }
    g.rows.push(row);
  }
  // Сортировка секций по последней активности (rows[0] — самая свежая в секции).
  const groups: ObjectGroup[] = [];
  for (const [key, g] of map) {
    const latestAt = g.rows.length > 0 ? Date.parse(g.rows[0]!.createdAt) : 0;
    groups.push({
      key,
      objectKind: g.kind,
      objectId: g.id,
      objectLabel: g.label,
      rows: g.rows,
      latestAt,
    });
  }
  groups.sort((a, b) => b.latestAt - a.latestAt);
  return groups;
}

function timeShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const t = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === new Date().toDateString()) return `сегодня, ${t}`;
  return `${d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}, ${t}`;
}

export function ChangesScreen({ router }: ChangesScreenProps): JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [rows, setRows] = useState<readonly ChangeRecordRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Ключи групп, у которых пользователь явно поменял состояние из дефолтного
  // (aidepo-28): группы с >1 записью свёрнуты по умолчанию, с 1 — развёрнуты.
  // Если ключ в множестве — состояние «инвертировано» относительно дефолта.
  const [toggled, setToggled] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    changes
      .listChanges({ limit: 200 })
      .then((res) => {
        if (cancelled) return;
        setRows(res.changes);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(humanizeError(err, 'load'));
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const groups = groupByObject(rows);

  const toggle = (key: string): void => {
    setToggled((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
      <ScreenHeader
        title="История"
        subtitle="Как менялось имущество"
        onBack={() => router.back()}
      />
      <div style={{ flex: 1, overflow: 'auto', padding: '4px 16px 24px' }}>
        {status === 'loading' && <Note text="Загружаю…" />}
        {status === 'error' && <Note text={error ?? 'Не удалось загрузить'} tone="danger" />}
        {status === 'ready' && groups.length === 0 && <Note text="Пока ничего не менялось." />}
        {status === 'ready' &&
          groups.map((g) => {
            const defaultOpen = g.rows.length === 1;
            const isOpen = toggled.has(g.key) ? !defaultOpen : defaultOpen;
            return (
              <ObjectSection
                key={g.key}
                group={g}
                expanded={isOpen}
                onToggle={() => toggle(g.key)}
                router={router}
              />
            );
          })}
      </div>
    </div>
  );
}

function ObjectSection({
  group,
  expanded,
  onToggle,
  router,
}: {
  readonly group: ObjectGroup;
  readonly expanded: boolean;
  readonly onToggle: () => void;
  readonly router: RouterApi;
}): JSX.Element {
  const kindRu = group.objectKind != null ? OBJECT_KIND_RU[group.objectKind] : null;
  const newest = group.rows[0]!;
  return (
    <div
      style={{
        marginTop: 14,
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: 14,
        overflow: 'hidden',
      }}
    >
      {/* aidepo-28: шапка-таггл — раскрывает/сворачивает раздел. */}
      <div
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 14px',
          background: T.surfaceAlt,
          borderBottom: expanded ? `1px solid ${T.divider}` : 'none',
          cursor: 'pointer',
        }}
      >
        <Avatar kind="ai" icon={objectIcon(group.objectKind)} size={32} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 700, color: T.ink, lineHeight: 1.25 }}>
            {group.objectLabel}
          </div>
          <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 2, fontWeight: 500 }}>
            {kindRu !== null ? `${kindRu} · ` : ''}
            {group.rows.length} {plural(group.rows.length)} · последнее{' '}
            {timeShort(newest.createdAt)}
          </div>
        </div>
        <Icon name={expanded ? 'chevD' : 'chevR'} size={18} color={T.ink3} />
      </div>
      {expanded && (
        <div>
          {group.rows.map((row, i) => (
            <ChangeRow
              key={row.id}
              row={row}
              isLast={i === group.rows.length - 1}
              objectLabel={group.objectLabel}
              router={router}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ChangeRow({
  row,
  isLast,
  objectLabel,
  router,
}: {
  readonly row: ChangeRecordRow;
  readonly isLast: boolean;
  readonly objectLabel: string;
  readonly router: RouterApi;
}): JSX.Element {
  const meta = KIND_META[row.kind] ?? { icon: 'history' as IconName, verb: 'Изменение' };
  const undone = row.undoneAt !== null;
  const expired = Date.parse(row.expiresAt) <= Date.now();
  const undoable = !undone && !expired;
  const actor = row.actorKind === 'ai' ? 'ассистент' : 'вы';

  // Если у записи есть entityLabel и он отличается от подписи объекта —
  // показываем его в строке (это нужно для правил ТО под оборудованием).
  const detail =
    row.entityLabel != null &&
    row.entityLabel.trim().length > 0 &&
    row.entityLabel.trim() !== objectLabel
      ? `«${row.entityLabel.trim()}»`
      : '';

  return (
    <div
      onClick={undoable ? () => router.go('undo', { id: row.id }) : undefined}
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        padding: '10px 14px',
        borderBottom: isLast ? 'none' : `1px solid ${T.divider}`,
        opacity: undone ? 0.55 : 1,
        cursor: undoable ? 'pointer' : 'default',
      }}
    >
      <Icon name={meta.icon} size={16} color={T.ink2} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, lineHeight: 1.3 }}>
          {detail.length > 0 ? `${meta.verb}: ${detail}` : meta.verb}
        </div>
        <div style={{ fontSize: 11, color: T.ink3, marginTop: 2, fontWeight: 500 }}>
          {timeShort(row.createdAt)} · {actor}
          {undone ? ' · откатано' : undoable ? ' · можно отменить' : ''}
        </div>
      </div>
      {undoable && <Icon name="undo" size={14} color={T.ink3} />}
    </div>
  );
}

function plural(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'изменение';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'изменения';
  return 'изменений';
}

function Note({
  text,
  tone,
}: {
  readonly text: string;
  readonly tone?: 'danger';
}): JSX.Element {
  return (
    <div
      style={{
        marginTop: 14,
        padding: '20px 14px',
        background: T.surfaceAlt,
        border: `1px solid ${T.border}`,
        borderRadius: 14,
        fontSize: 13,
        color: tone === 'danger' ? T.danger : T.ink3,
        textAlign: 'center',
        fontWeight: 500,
      }}
    >
      {text}
    </div>
  );
}
